import React, { createContext, useState, useEffect, useContext } from 'react';
import moment from 'moment-timezone';

const TZ = 'Pacific/Auckland';

const ReminderContext = createContext();

export const ReminderProvider = ({ children }) => {
  const [reminders, setReminders] = useState(() => {
    const savedReminders = localStorage.getItem('classReminders');
    if (savedReminders) {
      const parsedReminders = JSON.parse(savedReminders);
      const currentDate = moment().tz(TZ).format('YYYY-MM-DD');
      // Only keep reminders from today
      const filteredReminders = Object.fromEntries(
        Object.entries(parsedReminders).filter(([key]) => key.includes(currentDate))
      );
      return filteredReminders;
    }
    return {};
  });

  const [audioInitialized, setAudioInitialized] = useState(false);

  // Initialize audio context on user interaction
  const initializeAudio = () => {
    try {
      // Test speech synthesis
      const testUtterance = new SpeechSynthesisUtterance('Audio enabled');
      testUtterance.volume = 0; // Silent test
      window.speechSynthesis.speak(testUtterance);
      setAudioInitialized(true);
    } catch (error) {
      console.error('Error initializing audio:', error);
    }
  };

  useEffect(() => {
    localStorage.setItem('classReminders', JSON.stringify(reminders));
  }, [reminders]);

  // Check for class changes and clear reminders for new classes
  useEffect(() => {
    const storedClass = sessionStorage.getItem('currentClass');
    if (storedClass) {
      const currentClass = JSON.parse(storedClass);
      const classId = currentClass.id;
      const classDate = moment().tz(TZ).format('YYYY-MM-DD');
      const reminderKey = `${classId}-${classDate}`;
      
      // If reminders exist but are NOT for the current class, clear them
      const existingKeys = Object.keys(reminders);
      const isOldReminder = existingKeys.length > 0 && !existingKeys.includes(reminderKey);
      
      if (isOldReminder) {
        // Only keep reminders for this class, clear all others
        const newReminders = {};
        newReminders[reminderKey] = reminders[reminderKey] || {}; // Keep current if exists, otherwise empty
        setReminders(newReminders);
      }
    }
  }, [reminders]);

   const speakMessage = (text) => {
        const speech = new SpeechSynthesisUtterance(text);
        window.speechSynthesis.speak(speech);
    };

  const shouldProcessClassToday = (currentClass) => {
    const today = moment().tz(TZ).format('YYYY-MM-DD');
    if (currentClass.isSpecialClass) {
      return currentClass.specialClassDate === today;
    } else {
      const currentDayName = moment().tz(TZ).format('dddd');
      return currentClass.days.includes(currentDayName);
    }
  };

  const checkReminders = (currentClass, instructorLoginTimes) => {
    if (!currentClass) {
      return;
    }
    
    if (!shouldProcessClassToday(currentClass)) {
      return;
    }

    const classId = currentClass.id;
    const classDate = moment().tz(TZ).format('YYYY-MM-DD');
    const startTime = moment.tz(`${classDate} ${currentClass.startTime}`, 'YYYY-MM-DD HH:mm', TZ);
    const endTime = moment.tz(`${classDate} ${currentClass.endTime}`, 'YYYY-MM-DD HH:mm', TZ);
    const currentTime = moment().tz(TZ);
    const reminderActiveForInstructor = currentClass.isActive;
    const reminderActiveForAttendance = currentClass.isAttendanceReminder;
    // FIXED: Use classId in reminder key to distinguish between multiple classes on same day
    const reminderKey = `${classId}-${classDate}`;
    
    // FIXED: Only check instructors for THIS specific class
    const notLoggedInInstructors = currentClass.coaches.filter(coach => {
      // If instructorLoginTimes is null/undefined, coach is not logged in
      if (!instructorLoginTimes) return true;
      
      // Check if this coach has logged in
      const loginTime = instructorLoginTimes[coach];
      return !loginTime || loginTime === "Not logged in";
    });

    // Instructor login reminder - trigger immediately at 1 minute after class start (only once)
    const oneMinuteAfterStart = startTime.clone().add(1, 'minutes');
    
    if (notLoggedInInstructors.length > 0 && 
      currentTime.isBetween(oneMinuteAfterStart, endTime, null, '[)') &&
      !reminders[reminderKey]?.login && 
      reminderActiveForInstructor) {
      
      const instructorNames = notLoggedInInstructors.join(', ');
      const message = `${notLoggedInInstructors.length > 1 ? 'Instructors' : 'Instructor'} ${instructorNames}, please check in`;
      
      // Speak 3 times immediately
      //for (let i = 0; i < 3; i++) {
        speakMessage(message);
      //}
      
      setReminders(prev => ({ 
        ...prev, 
        [reminderKey]: { ...prev[reminderKey], login: true } 
      }));
    }

    // Attendance reminder - trigger at 30 minutes after class start (only once)
    const thirtyMinutesAfterStart = startTime.clone().add(30, 'minutes');
    
    if (currentTime.isBetween(thirtyMinutesAfterStart, endTime, null, '[)') &&
      !reminders[reminderKey]?.attendance && 
      reminderActiveForAttendance) {
      
      const message = `Reminder: Please check attendance for ${currentClass.className}.`;
      
      // Speak once only
      speakMessage(message);
      
      setReminders(prev => ({ 
        ...prev, 
        [reminderKey]: { ...prev[reminderKey], attendance: true } 
      }));
    }
  };

  const checkSessionStorageAndReminders = () => {
    const storedClass = sessionStorage.getItem('currentClass');
    const storedInstructorLoginTimes = sessionStorage.getItem('instructorLoginTimes');
    
    if (storedClass) {
      const currentClass = JSON.parse(storedClass);
      const instructorLoginTimes = storedInstructorLoginTimes 
        ? JSON.parse(storedInstructorLoginTimes) 
        : null;
      
      checkReminders(currentClass, instructorLoginTimes);
    } else {
    }
  };

  return (
    <ReminderContext.Provider value={{ reminders, setReminders, checkReminders, checkSessionStorageAndReminders, initializeAudio, audioInitialized }}>
      {children}
    </ReminderContext.Provider>
  );
};

export const useReminders = () => useContext(ReminderContext);